import { Capacitor } from "@capacitor/core";

/**
 * Ads.
 *   Native (Capacitor): AdMob banner at the bottom, interstitial after selected
 *   actions (rate limited), rewarded ads that unlock 24-hour perks. The reward
 *   is granted server-side: through AdMob SSV (custom_data = "<userId>:<kind>")
 *   when configured, else by a client claim that the server accepts only when
 *   REWARD_ALLOW_CLIENT_CLAIM=1.
 *   Web: an AdSense slot component (components/AdBanner.tsx). AdSense has no
 *   rewarded format for ordinary sites, so rewards are app-only on the web.
 * All ids come from VITE_ADMOB_* / VITE_ADSENSE_* env vars; without them the
 * module is a no-op so development builds never request ads.
 */

const base = import.meta.env.VITE_API_ORIGIN ?? "";
const env = import.meta.env;

export const adsConfig = {
  admobBanner: env.VITE_ADMOB_BANNER_ID ?? "",
  admobInterstitial: env.VITE_ADMOB_INTERSTITIAL_ID ?? "",
  admobRewarded: env.VITE_ADMOB_REWARDED_ID ?? "",
  admobTesting: env.VITE_ADMOB_TESTING === "1",
  adsenseClient: env.VITE_ADSENSE_CLIENT ?? "",
  adsenseSlot: env.VITE_ADSENSE_SLOT ?? "",
};

export const isNative = (): boolean => Capacitor.isNativePlatform();
export const nativeAdsEnabled = (): boolean => isNative() && !!adsConfig.admobBanner;
export const rewardedAvailable = (): boolean => isNative() && !!adsConfig.admobRewarded;

let initialized = false;
let adFreeUntil: string | null = null;
let lastInterstitialAt = 0;
const INTERSTITIAL_MIN_INTERVAL_MS = 3 * 60 * 1000;

export function setAdFreeUntil(until: string | null): void {
  adFreeUntil = until;
  if (isAdFree()) void hideBanner();
}

export const isAdFree = (): boolean => !!adFreeUntil && adFreeUntil > new Date().toISOString();

async function admob() {
  const { AdMob } = await import("@capacitor-community/admob");
  return AdMob;
}

/** Initialize the SDK, run the consent flow (UMP / ATT) and show the banner. */
export async function initAds(): Promise<void> {
  if (!nativeAdsEnabled() || initialized) return;
  initialized = true;
  const AdMob = await admob();
  await AdMob.initialize({ initializeForTesting: adsConfig.admobTesting });
  try {
    const { AdmobConsentStatus } = await import("@capacitor-community/admob");
    const info = await AdMob.requestConsentInfo();
    if (info.isConsentFormAvailable && info.status === AdmobConsentStatus.REQUIRED) await AdMob.showConsentForm();
    await AdMob.requestTrackingAuthorization();
  } catch {
    // Consent is best-effort; ads still load as non-personalized where required.
  }
  if (!isAdFree()) await showBanner();
}

export async function showBanner(): Promise<void> {
  if (!nativeAdsEnabled() || isAdFree()) return;
  const AdMob = await admob();
  const { BannerAdPosition, BannerAdSize } = await import("@capacitor-community/admob");
  await AdMob.showBanner({
    adId: adsConfig.admobBanner,
    adSize: BannerAdSize.ADAPTIVE_BANNER,
    position: BannerAdPosition.BOTTOM_CENTER,
    margin: 56, // above the tab bar
    isTesting: adsConfig.admobTesting,
  });
}

export async function hideBanner(): Promise<void> {
  if (!nativeAdsEnabled()) return;
  try {
    await (await admob()).hideBanner();
  } catch {
    // no banner shown
  }
}

/** Show an interstitial at a natural break (after saving), at most once per interval. */
export async function maybeShowInterstitial(): Promise<void> {
  if (!isNative() || !adsConfig.admobInterstitial || isAdFree()) return;
  if (Date.now() - lastInterstitialAt < INTERSTITIAL_MIN_INTERVAL_MS) return;
  lastInterstitialAt = Date.now();
  try {
    const AdMob = await admob();
    await AdMob.prepareInterstitial({ adId: adsConfig.admobInterstitial, isTesting: adsConfig.admobTesting });
    await AdMob.showInterstitial();
  } catch {
    // Failing to show an ad must never block the user.
  }
}

export type RewardKind = "ad_free" | "csv_export";

/**
 * Show a rewarded ad and return once the reward is granted (or throw).
 * The server either receives the AdMob SSV callback or accepts the client claim.
 */
export async function showRewarded(userId: string, kind: RewardKind): Promise<{ until: string }> {
  if (!rewardedAvailable()) throw new Error("リワード広告はアプリ版でのみ利用できます");
  const AdMob = await admob();
  const { RewardAdPluginEvents } = await import("@capacitor-community/admob");
  await AdMob.prepareRewardVideoAd({
    adId: adsConfig.admobRewarded,
    isTesting: adsConfig.admobTesting,
    ssv: { userId, customData: `${userId}:${kind}` },
  });
  const rewarded = new Promise<void>((resolve) => {
    void AdMob.addListener(RewardAdPluginEvents.Rewarded, () => resolve());
  });
  await AdMob.showRewardVideoAd();
  await rewarded;

  // Try a client claim; when SSV is the only accepted path the server answers 403 and the
  // reward arrives through the callback shortly after.
  const transactionId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const res = await fetch(`${base}/api/me/rewards/claim`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ kind, transactionId }),
  });
  if (res.ok) {
    const body = (await res.json()) as { until: string };
    if (kind === "ad_free") setAdFreeUntil(body.until);
    return body;
  }
  // SSV path: poll the reward state briefly.
  for (let i = 0; i < 10; i++) {
    await new Promise((r) => setTimeout(r, 1500));
    const st = await fetch(`${base}/api/me/rewards`, { credentials: "include" }).then((r) => r.json() as Promise<{ adFreeUntil: string | null; csvExportUntil: string | null }>);
    const until = kind === "ad_free" ? st.adFreeUntil : st.csvExportUntil;
    if (until && until > new Date().toISOString()) {
      if (kind === "ad_free") setAdFreeUntil(until);
      return { until };
    }
  }
  throw new Error("報酬の確認に失敗しました。しばらくしてから設定画面で確認してください");
}
